import React, { useState } from 'react';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, Grid, InputLabel, MenuItem, Select, TextField, Typography,
  Switch, FormControlLabel, Divider, Alert, Stack, useTheme, CircularProgress
} from '@mui/material';
import { supabase } from '../../services/supabaseClient';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';

interface Props {
  open: boolean;
  onClose: () => void;
  prefill?: {
    commodity_name?: string;
    category_key?: string;
    title?: string;
  };
}

const CATEGORIES = [
  { key: 'produce',   label: 'Agricultural Produce' },
  { key: 'inputs',    label: 'Farm Inputs / Equipment' },
  { key: 'minerals',  label: 'Minerals & Ores' },
  { key: 'materials', label: 'Materials / Commodities' },
  { key: 'other',     label: 'Other Bulk Items' },
];

const SCOPES = [
  { key: 'local',           label: 'Local delivery only' },
  { key: 'export',          label: 'International / export' },
  { key: 'local_or_export', label: 'Either is fine' },
];

const UNITS = ['kg','tonne','piece','bag (90kg)','bag (50kg)','dozen','box','crate','crate (30kg)','m³','litre','unit'];

const BuyerRequestFormDialog: React.FC<Props> = ({ open, onClose, prefill }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [sending, setSending] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [form, setForm] = useState<any>(() => ({
    title: prefill?.title ?? '',
    commodity_name: prefill?.commodity_name ?? '',
    category_key: prefill?.category_key ?? 'produce',
    quantity: '',
    unit: 'kg',
    quality_grade: '',
    specification_notes: '',
    packaging_requirements: '',
    target_price_per_unit: '',
    currency: 'KES',
    scope: 'local_or_export',
    delivery_location: '',
    delivery_country: '',
    delivery_city: '',
    requested_delivery_date: '',
    is_recurring: false,
    recurrence_schedule: '',
    buyer_company_name: '',
    buyer_contact_name: '',
    buyer_contact_phone: '',
    buyer_contact_email: '',
    buyer_business_type: '',
  }));

  const update = (k: string, v: any) => setForm((prev: any) => ({ ...prev, [k]: v }));

  const onSubmit = async () => {
    if (!form.title || !form.commodity_name || !form.quantity || !form.delivery_location) {
      setErrorMsg('Fill in title, item, quantity, and delivery location.');
      setSuccessMsg(null);
      return;
    }
    setSending(true);
    setErrorMsg(null);
    setSuccessMsg(null);
    try {
      const { data, error } = await supabase.rpc('create_buyer_request', {
        p_title: form.title,
        p_commodity_name: form.commodity_name,
        p_category_key: form.category_key,
        p_quantity: Number(form.quantity) || null,
        p_unit: form.unit,
        p_quality_grade: form.quality_grade || null,
        p_specification_notes: form.specification_notes || null,
        p_packaging_requirements: form.packaging_requirements || null,
        p_target_price_per_unit: Number(form.target_price_per_unit) || null,
        p_currency: form.currency,
        p_scope: form.scope,
        p_delivery_location: form.delivery_location,
        p_delivery_country: form.delivery_country || null,
        p_delivery_city: form.delivery_city || null,
        p_requested_delivery_date: form.requested_delivery_date || null,
        p_is_recurring: !!form.is_recurring,
        p_recurrence_schedule: form.recurrence_schedule || null,
        p_buyer_company_name: form.buyer_company_name || null,
        p_buyer_contact_name: form.buyer_contact_name || null,
        p_buyer_contact_phone: form.buyer_contact_phone || null,
        p_buyer_contact_email: form.buyer_contact_email || null,
        p_buyer_business_type: form.buyer_business_type || null,
      });
      if (error) throw error;
      setSuccessMsg(`Sourcing request created: ${data.request_code}. We'll contact you shortly.`);
      window.setTimeout(() => onClose(), 900);
    } catch (e: any) {
      setErrorMsg(e?.message ?? 'Failed to submit request');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
        <Box sx={{
          bgcolor: isDark ? 'rgba(102,187,106,0.18)' : 'rgba(102,187,106,0.12)',
          color: 'primary.main', borderRadius: 2, p: 1.2
        }}>
          <AddShoppingCartIcon />
        </Box>
        <Box>
          <Typography variant="h6">Request Sourcing (What do you need?)</Typography>
          <Typography variant="body2" color="text.secondary">
            Tell us specs, quantity and destination. FarmersHub sources, quality-checks, and delivers locally or for export.
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 2 }}>
        {errorMsg && <Alert severity="error" sx={{ mb: 2, borderRadius: 3 }} onClose={() => setErrorMsg(null)}>{errorMsg}</Alert>}
        {successMsg && <Alert severity="success" sx={{ mb: 2, borderRadius: 3 }} onClose={() => setSuccessMsg(null)}>{successMsg}</Alert>}
        <Alert severity="info" sx={{ mb: 2 }}>
          Works for agricultural produce, farm inputs, and future verticals like minerals or other commodities.
        </Alert>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth label="Request title" required
              placeholder="e.g. Monthly supply of fresh avocado"
              value={form.title} onChange={(e) => update('title', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select label="Category" value={form.category_key} onChange={(e) => update('category_key', e.target.value)}>
                {CATEGORIES.map((c) => <MenuItem key={c.key} value={c.key}>{c.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={7}>
            <TextField fullWidth label="Commodity / Item name" required
              placeholder="e.g. Hass avocado (grade A), maize, fertilizer DAP, construction sand"
              value={form.commodity_name} onChange={(e) => update('commodity_name', e.target.value)} />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField fullWidth type="number" label="Quantity" required
              inputProps={{ min: 0, step: 'any' }}
              value={form.quantity} onChange={(e) => update('quantity', e.target.value)} />
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Unit</InputLabel>
              <Select label="Unit" value={form.unit} onChange={(e) => update('unit', e.target.value)}>
                {UNITS.map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} md={4}>
            <TextField fullWidth label="Grade / Quality"
              placeholder="e.g. Grade A / 13% moisture / 20kg bag"
              value={form.quality_grade} onChange={(e) => update('quality_grade', e.target.value)} />
          </Grid>
          <Grid item xs={6} md={4}>
            <TextField fullWidth label="Target price (per unit, optional)"
              type="number" inputProps={{ min: 0, step: 'any' }}
              value={form.target_price_per_unit} onChange={(e) => update('target_price_per_unit', e.target.value)} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Currency" value={form.currency}
              onChange={(e) => update('currency', e.target.value)} />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Delivery scope</InputLabel>
              <Select label="Delivery scope" value={form.scope} onChange={(e) => update('scope', e.target.value)}>
                {SCOPES.map((s) => <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField fullWidth label="Delivery location" required
              placeholder="Full address / warehouse / port of destination"
              value={form.delivery_location} onChange={(e) => update('delivery_location', e.target.value)} />
          </Grid>
          <Grid item xs={6} md={4}>
            <TextField fullWidth label="Country" value={form.delivery_country}
              onChange={(e) => update('delivery_country', e.target.value)} />
          </Grid>
          <Grid item xs={6} md={4}>
            <TextField fullWidth label="City / Town" value={form.delivery_city}
              onChange={(e) => update('delivery_city', e.target.value)} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth type="date" label="Requested delivery by" InputLabelProps={{ shrink: true }}
              value={form.requested_delivery_date} onChange={(e) => update('requested_delivery_date', e.target.value)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Specifications / quality notes" multiline minRows={2}
              placeholder="Size, color, defects tolerance, organic requirement, standards (e.g. EU MRL), etc."
              value={form.specification_notes} onChange={(e) => update('specification_notes', e.target.value)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Packaging requirements" multiline minRows={2}
              placeholder="Branded sacks, cold chain, palletized, 20ft container, etc."
              value={form.packaging_requirements} onChange={(e) => update('packaging_requirements', e.target.value)} />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={form.is_recurring}
                onChange={(e) => update('is_recurring', e.target.checked)} />}
              label="This is a recurring / standing order"
            />
            {form.is_recurring && (
              <TextField fullWidth size="small" placeholder="e.g. Monthly, Every 2 weeks, Qty 5t/month"
                sx={{ mt: 1 }}
                value={form.recurrence_schedule} onChange={(e) => update('recurrence_schedule', e.target.value)} />
            )}
          </Grid>

          <Grid item xs={12}>
            <Divider>Buyer details</Divider>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Company / Organization"
              value={form.buyer_company_name} onChange={(e) => update('buyer_company_name', e.target.value)} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Contact name"
              value={form.buyer_contact_name} onChange={(e) => update('buyer_contact_name', e.target.value)} />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Buyer type</InputLabel>
              <Select label="Buyer type" value={form.buyer_business_type} onChange={(e) => update('buyer_business_type', e.target.value)}>
                {['Individual','Retailer / Supermarket','Restaurant / Hotel','Institution (school/hospital)','Processor / Manufacturer','Importer / Wholesaler','E-commerce / Subscription','NGO / Relief','Government / Parastatal','Other'].map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={6}>
            <TextField fullWidth label="Phone" value={form.buyer_contact_phone}
              onChange={(e) => update('buyer_contact_phone', e.target.value)} />
          </Grid>
          <Grid item xs={6} md={6}>
            <TextField fullWidth label="Email" value={form.buyer_contact_email}
              onChange={(e) => update('buyer_contact_email', e.target.value)} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={sending}>Cancel</Button>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Chip size="small" color="success" variant="outlined" label="Local + Export" />
          <Chip size="small" color="primary" variant="outlined" label="Any category" />
          <Button variant="contained" onClick={onSubmit} startIcon={sending ? <CircularProgress size={16} /> : <AddShoppingCartIcon />} disabled={sending}>
            Submit Sourcing Request
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default BuyerRequestFormDialog;
